/**
 * Dynamic Lookup Functions (Haiku-powered)
 *
 * These functions call backend APIs that use Claude Haiku for
 * fast, cheap lookups when static data is missing.
 *
 * All functions have aggressive caching on the backend (7-30 days)
 * to minimize API costs.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Auth headers helper
const getApiHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const { getSession } = await import('./supabase');
    const session = await getSession();
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  } catch { /* proceed without auth */ }
  return headers;
};

// In-memory cache for session
const sessionCache = new Map();

const getCached = (key) => {
  const cached = sessionCache.get(key);
  if (cached && Date.now() < cached.expiry) {
    return cached.data;
  }
  sessionCache.delete(key);
  return null;
};

const setCache = (key, data, ttlMs = 60 * 60 * 1000) => {
  sessionCache.set(key, { data, expiry: Date.now() + ttlMs });
};

/**
 * Get visa requirements for passport(s) traveling to destination
 * @param {string|string[]} passportCountries - Single passport or array of passports
 * @param {string} destination - e.g., "Japan", "Brazil", "France"
 * @returns {object} - Best visa option across all passports
 */
export const getVisaRequirements = async (passportCountries, destination) => {
  if (!passportCountries || !destination) return null;

  // Normalize to array
  const passports = Array.isArray(passportCountries) ? passportCountries : [passportCountries];
  if (passports.length === 0) return null;

  // For multiple passports, check each and return the best option (least restrictive)
  const results = await Promise.all(
    passports.map(async (passport) => {
      const cacheKey = `visa:${passport}:${destination}`;
      const cached = getCached(cacheKey);
      if (cached) return cached;

      try {
        const response = await fetch(`${BACKEND_URL}/api/visa-requirements`, {
          method: 'POST',
          headers: await getApiHeaders(),
          body: JSON.stringify({ passportCountry: passport, destination })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setCache(cacheKey, data, 24 * 60 * 60 * 1000);
            return data;
          }
        }
      } catch (error) {
        console.error(`Visa lookup failed for ${passport}:`, error);
      }
      return null;
    })
  );

  // Filter successful results and find best option
  const validResults = results.filter(r => r !== null);
  if (validResults.length === 0) return null;

  // Rank: no visa > visa on arrival > evisa > visa required
  const visaRank = { 'none': 0, 'visa_on_arrival': 1, 'evisa': 2, 'tourist': 3, 'unknown': 4 };
  validResults.sort((a, b) => (visaRank[a.visaType] || 4) - (visaRank[b.visaType] || 4));

  const best = validResults[0];
  if (passports.length > 1) {
    best.recommendedPassport = best.passportCountry;
    best.allResults = validResults;
  }

  return best;
};

/**
 * Get currency for a country
 * @param {string} country - e.g., "Japan", "Switzerland"
 */
export const getCountryCurrency = async (country) => {
  if (!country) return null;

  const cacheKey = `currency:${country}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/country-currency`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ country })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setCache(cacheKey, data, 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Currency lookup failed:', error);
  }

  return null;
};

/**
 * Get border crossing information
 * @param {string} fromCountry - e.g., "France"
 * @param {string} toCountry - e.g., "Germany"
 * @param {string} passportCountry - optional, e.g., "United States"
 */
export const getBorderInfo = async (fromCountry, toCountry, passportCountry = null) => {
  if (!fromCountry || !toCountry) return null;

  const cacheKey = `border:${fromCountry}:${toCountry}:${passportCountry || 'any'}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/border-info`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ fromCountry, toCountry, passportCountry })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setCache(cacheKey, data, 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Border info lookup failed:', error);
  }

  return null;
};

/**
 * Get timezone for a city
 * @param {string} city - e.g., "Tokyo", "New York"
 * @param {string} country - optional, e.g., "Japan"
 */
export const getCityTimezone = async (city, country = null) => {
  if (!city) return null;

  const cacheKey = `tz:${city}:${country || ''}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/timezone-haiku`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ city, country })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setCache(cacheKey, data, 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Timezone lookup failed:', error);
  }

  return null;
};

/**
 * Get airports for a city (checks if multi-airport)
 * @param {string} city - e.g., "New York", "London"
 */
export const getCityAirports = async (city) => {
  if (!city) return null;

  const cacheKey = `airports:${city}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/city-airports`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ city })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setCache(cacheKey, data, 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Airport lookup failed:', error);
  }

  return null;
};

/**
 * Get optimal transport mode between cities
 * @param {string} fromCity - e.g., "Paris"
 * @param {string} toCity - e.g., "Rome"
 */
export const getOptimalTransport = async (fromCity, toCity) => {
  if (!fromCity || !toCity) return null;

  const cacheKey = `transport:${fromCity}:${toCity}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/quick-transport`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ fromCity, toCity })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setCache(cacheKey, data, 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Transport lookup failed:', error);
  }

  return null;
};

/**
 * Get geographic features of a destination (island detection, access methods)
 * @param {string} destination - e.g., "Bazaruto Island", "Santorini", "Zermatt"
 * @param {string} country - optional, e.g., "Mozambique", "Greece"
 * @returns {object} - Geographic features including isIsland, accessMethods, etc.
 */
export const getDestinationGeography = async (destination, country = null) => {
  if (!destination) return null;

  const cacheKey = `geo:${destination}:${country || ''}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/destination-geography`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ destination, country })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        // Cache for 24 hours in session (backend caches for 365 days)
        setCache(cacheKey, data, 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Geography lookup failed:', error);
  }

  return null;
};

/**
 * Check if a destination is an island (convenience wrapper)
 * @param {string} destination - e.g., "Bazaruto Island"
 * @param {string} country - optional
 * @returns {boolean} - true if destination is an island
 */
export const isIslandDestination = async (destination, country = null) => {
  const geo = await getDestinationGeography(destination, country);
  return geo?.isIsland || geo?.isArchipelago || false;
};

/**
 * Get access methods for a destination (e.g., ["ferry", "flight"] for islands)
 * @param {string} destination - e.g., "Bazaruto Island"
 * @param {string} country - optional
 * @returns {string[]} - Array of access methods
 */
export const getAccessMethods = async (destination, country = null) => {
  const geo = await getDestinationGeography(destination, country);
  return geo?.accessMethods || ['flight', 'car'];
};

/**
 * Get detailed inter-city travel options (uses Sonnet for comprehensive data)
 * @param {string} fromCity - e.g., "Lima"
 * @param {string} toCity - e.g., "Cusco"
 */
export const getDetailedTravelOptions = async (fromCity, toCity) => {
  if (!fromCity || !toCity) return null;

  const cacheKey = `travel-detailed:${fromCity}:${toCity}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/detect-intercity-travel`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ fromCity, toCity })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setCache(cacheKey, data, 60 * 60 * 1000); // 1 hour (uses Sonnet, more expensive)
        return data;
      }
    }
  } catch (error) {
    console.error('Detailed travel lookup failed:', error);
  }

  return null;
};

/**
 * Get booking requirements for an attraction
 * @param {string} attraction - e.g., "Alhambra", "Anne Frank House"
 * @param {string} city - optional, e.g., "Granada"
 * @param {string} country - optional, e.g., "Spain"
 * @returns {object} - Booking info including lead time, timed entry, etc.
 */
export const getAttractionBookingInfo = async (attraction, city = null, country = null) => {
  if (!attraction) return null;

  const cacheKey = `booking:${attraction}:${city || ''}:${country || ''}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/attraction-booking-info`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ attraction, city, country })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        // Cache for 24 hours in session (backend caches for 30 days)
        setCache(cacheKey, data, 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Booking info lookup failed:', error);
  }

  return null;
};

/**
 * Get health and safety information for a destination
 * @param {string} destination - e.g., "Cusco", "Bangkok"
 * @param {string} country - optional, e.g., "Peru", "Thailand"
 * @returns {object} - Health/safety info including tap water, vaccinations, scams, etc.
 */
export const getDestinationHealthSafety = async (destination, country = null) => {
  if (!destination) return null;

  const cacheKey = `health-safety:${destination}:${country || ''}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${BACKEND_URL}/api/destination-health-safety`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ destination, country })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        // Cache for 7 days in session (backend caches for 90 days)
        setCache(cacheKey, data, 7 * 24 * 60 * 60 * 1000);
        return data;
      }
    }
  } catch (error) {
    console.error('Health/safety lookup failed:', error);
  }

  return null;
};

/**
 * Check if destination requires altitude acclimatization
 * @param {string} destination - e.g., "Cusco"
 * @param {string} country - optional
 * @returns {object|null} - Altitude info if high altitude, null otherwise
 */
export const getAltitudeInfo = async (destination, country = null) => {
  const geo = await getDestinationGeography(destination, country);
  if (geo?.altitude?.requiresAcclimatization) {
    return geo.altitude;
  }
  return null;
};

/**
 * Check if destination requires permits
 * @param {string} destination - e.g., "Bhutan", "Machu Picchu"
 * @param {string} country - optional
 * @returns {object|null} - Permit info if required, null otherwise
 */
export const getPermitInfo = async (destination, country = null) => {
  const geo = await getDestinationGeography(destination, country);
  if (geo?.permits?.required) {
    return geo.permits;
  }
  return null;
};

/**
 * Get driving information for a destination
 * @param {string} destination - e.g., "Tokyo", "London"
 * @param {string} country - optional
 * @returns {object|null} - Driving info including left-hand drive, IDP requirements
 */
export const getDrivingInfo = async (destination, country = null) => {
  const geo = await getDestinationGeography(destination, country);
  return geo?.driving || null;
};

/**
 * Validate a day's activities for geographic feasibility
 * Uses Haiku to check for unrealistic day trips, wrong locations, etc.
 * @param {object} dayData - The day's schedule and meals
 * @param {string} baseCity - The base city for this day
 * @param {string} hotelLocation - Optional hotel location for context
 * @param {string} tripType - 'destination' or 'hotel'
 * @param {number} dayNumber - Day number in the trip
 * @param {number} totalDays - Total days in trip
 * @returns {object} - { valid, issues, overallAssessment }
 */
export const validateDayActivities = async (dayData, baseCity, hotelLocation = null, tripType = 'destination', dayNumber = 1, totalDays = 1) => {
  if (!dayData || !baseCity) return { valid: true, issues: [], overallAssessment: 'No data to validate' };

  try {
    const response = await fetch(`${BACKEND_URL}/api/validate-day-activities`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({
        dayData,
        baseCity,
        hotelLocation,
        tripType,
        dayNumber,
        totalDays
      })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        return data;
      }
    }
  } catch (error) {
    console.error('Day validation failed:', error);
  }

  // Return valid on error to not block generation
  return { valid: true, issues: [], overallAssessment: 'Validation unavailable' };
};

/**
 * Regenerate a single activity that was flagged as problematic
 * @param {string} problemActivity - Name of the activity to replace
 * @param {string} problem - Description of why it's problematic
 * @param {string} baseCity - The base city
 * @param {string} hotelLocation - Optional hotel location
 * @param {string} dayTheme - Theme of the day
 * @param {string[]} existingActivities - List of activities already in itinerary (to avoid)
 * @param {string} timeSlot - Time slot for the activity
 * @param {string} activityType - 'activity' or 'meal'
 * @returns {object} - Replacement activity data
 */
export const regenerateActivity = async (problemActivity, problem, baseCity, hotelLocation = null, dayTheme = null, existingActivities = [], timeSlot = null, activityType = 'activity') => {
  if (!problemActivity || !baseCity) return null;

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000); // 30s timeout
    const response = await fetch(`${BACKEND_URL}/api/regenerate-activity`, {
      method: 'POST',
      headers: await getApiHeaders(),
      signal: controller.signal,
      body: JSON.stringify({
        problemActivity,
        problem,
        baseCity,
        hotelLocation,
        dayTheme,
        existingActivities,
        timeSlot,
        activityType
      })
    });
    clearTimeout(timeout);

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.replacement) {
        return data.replacement;
      }
      console.warn('Regeneration returned unsuccessful:', data.error || 'no replacement data');
      return { _error: data.error || 'No replacement found' };
    } else {
      const errorText = await response.text().catch(() => '');
      console.warn(`Regeneration HTTP ${response.status}:`, errorText.slice(0, 200));
      return { _error: `Server error (${response.status})` };
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Activity regeneration timed out after 30s');
      return { _error: 'Request timed out — please try again' };
    } else {
      console.error('Activity regeneration failed:', error);
      return { _error: 'Network error — check your connection' };
    }
  }
};

export default {
  getVisaRequirements,
  getCountryCurrency,
  getBorderInfo,
  getCityTimezone,
  getCityAirports,
  getOptimalTransport,
  getDetailedTravelOptions,
  getDestinationGeography,
  isIslandDestination,
  getAccessMethods,
  getAttractionBookingInfo,
  getDestinationHealthSafety,
  getAltitudeInfo,
  getPermitInfo,
  getDrivingInfo,
  validateDayActivities,
  regenerateActivity,
};
